import React from "react";
import Paper from "@mui/material/Paper";
import AmCharts from "@amcharts/amcharts3-react";
import Backend from "services/Backend";
import { getFullProductNameSplit, chainTypeToString, getMaxGridCount, getWeekFromDateString, getShortDateWithWeek } from "../../../services/Utils";
import { injectIntl } from "react-intl";

const CHART_COLORS = ["#67b7dc", "#fdd400", "#84b761", "#cc4748", "#cd82ad", "#2f4074", "#448e4d", "#b7b83f", "#b9783f", "#b93e3d", "#913167"];

class StorePricePlot extends React.Component {
  constructor(props) {
    super(props);
    this.chartInstance = null;
    this.legendItemEls = {};
    this.legendValueEls = {};
    this.cursorLocked = false;
    this.lockedCursorIndex = null;
    this.lockedCategory = null;
    this.lockedBulletItems = [];
    this._snappingCursor = false;
    this.chartListeners = [
      { event: "init", method: this.handleChartInit },
      { event: "changed", method: this.handleCursorChanged },
    ];
    this.cursorListeners = [{ event: "onHideCursor", method: this.handleCursorHidden }];
  }

  state = {
    productData: [],
    plotLayout: {
      title: "",
      yaxis: {
        title: "",
        ticksuffix: "",
      },
      xaxis: {
        title: "",
      },
    },
    id: this.props.stores[0].id,
    fileName: this.props.stores.length == 1 ? this.props.stores[0].chainName + " " + this.props.stores[0].city : (this.props.stores[0].chainName || this.props.intl.formatMessage({ id: "store.other" })) + " " + chainTypeToString(this.props.stores[0].type) + " " + new Date().toJSON().slice(0, 10),
    activeProductsCount: 0,
    graphs: [],
    plotData: [],
    weeksCount: 5,
  };

  componentDidMount() {
    if (this.props.stores) {
      if (this.props.stores.some((x) => x.active)) {
        this.parseDataForPlot();
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.stores.length !== this.props.stores.length || prevProps.showDiscountPrices !== this.props.showDiscountPrices || prevProps.showComparePrices !== this.props.showComparePrices || this.getActiveProductsFromStores(this.props.stores).length !== this.state.activeProductsCount) {
      if (this.props.stores.some((x) => x.active)) {
        this.parseDataForPlot();
      }
    }
  }

  unitConvertion = {
    1: "kg", // Weight units, converted to kilograms
    2: "l", // Volume units, converted to liters
    3: "st", // Singe units, not used for now
    4: "m", // Length units, converted to meters
  };

  getActiveProductsFromStores(stores) {
    const allActiveProducts = stores.map((x) => x.getActiveProducts());
    let uniqueProducts = allActiveProducts.flatMap((x) => x);
    uniqueProducts = Array.from(
      uniqueProducts
        .reduce((acc, val) => {
          if (!acc.has(val.id)) {
            acc.set(val.id, val);
          }
          return acc;
        }, new Map())
        .values(),
    );

    return uniqueProducts;
  }

  parseDataForPlot = () => {
    const stores = this.props.stores;
    const uniqueProducts = this.getActiveProductsFromStores(stores);
    const activeProducts = uniqueProducts;
    const productData = this.createDataForProducts(stores, activeProducts);
    const plotData = this.createPlotData(productData);
    const plotLayout = this.createLayoutForStore(stores);

    this.setState(
      {
        activeProductsCount: activeProducts.length,
        stores: stores,
        productData,
        plotData,
        plotLayout,
        graphs: this.createGraphs(productData, stores[0], plotLayout),
      },
      () => {
        this.restoreCursorLock(this.chartInstance);
      },
    );
  };

  formatAmountToPrice(amount) {
    const amountFloat = parseFloat(amount);
    if (!isNaN(amountFloat)) {
      return (amountFloat / 100).toFixed(2);
    }
    return null;
  }

  formatLegendText(name, manufacturer, width) {
    name = name === null ? "" : name;
    manufacturer = manufacturer === null ? "" : manufacturer;

    const stringDivider = (str, w) => {
      if (str.length > w) {
        let p = w;
        while (p > 0 && !/\s/.test(str[p])) {
          p--;
        }
        if (p > 0) {
          const left = str.substring(0, p);
          const right = str.substring(p + 1);
          return left + "<br>" + stringDivider(right, w);
        }
      }
      return str;
    };

    if (name.length + manufacturer.length <= width - 2) {
      return name + ", " + manufacturer;
    }

    const splitname = name.length > width ? stringDivider(name, width) + "<br>" : name + "<br>";
    const splitmanu = manufacturer.length > width ? stringDivider(manufacturer, width) : manufacturer;

    return splitname + splitmanu;
  }

  createDataForProducts(stores, activeProducts) {
    const returnData = [];
    const storeIds = new Set(...stores.map((x) => x.id));
    const priceDates = activeProducts
      .flatMap((cp) => cp.prices.flatMap((p) => p))
      .filter((p) => storeIds.has(p.storeId))
      .flatMap((p) => {
        if (p.lastDateTime) {
          const db = new Date(p.lastDateTime);
          db.setDate(db.getDate() - 1);
          return [p.datetime, db.datetime, p.lastDateTime];
        }
        return [p.datetime];
      })
      .flatMap((p) => p)
      .filter((value, index, self) => {
        return self.indexOf(value) === index;
      })
      .map((x) => new Date(x))
      .sort((a, b) => {
        return a - b;
      });
    activeProducts.forEach((product) => {
      const allInnerPrices = stores.map((x) => product.getPricesByStore(x.id, priceDates));
      const innerPrices = this.averageInnerPrices(allInnerPrices);
      innerPrices.forEach((prices) => {
        const data = {};
        const splitName = getFullProductNameSplit(product);
        data.productName = splitName[0];
        if (data.productName.length > 23) {
          data.productName = data.productName.substring(0, 23) + "…";
        }
        data.name = this.formatLegendText(splitName[0], splitName[1], 30); // Legend 30 characters wide max
        data.line = {
          size: 10,
          shape: "hv",
        };
        const xData = [];
        // const yData = [];
        const amChartData = [];
        const text = [];

        for (let i = 0; i < prices.length; i++) {
          let tempText = "";
          xData.push(prices[i].date);
          if (prices[i].discountAmount && this.props.showDiscountPrices && !this.props.showComparePrices) {
            amChartData.push({
              date: prices[i].date,
              price: this.formatAmountToPrice(prices[i].discountAmount),
            });
            // yData.push(this.formatAmountToPrice(prices[i].discountAmount));
            tempText = "Kampanjpris!";
            if (prices[i].onlyMembers) {
              tempText += " Kortvara";
            }
          } else if (prices[i].compareDiscountAmount && this.props.showDiscountPrices && this.props.showComparePrices) {
            amChartData.push({
              date: prices[i].date,
              price: this.formatAmountToPrice(prices[i].compareDiscountAmount),
            });
            // yData.push(
            //  this.formatAmountToPrice(prices[i].compareDiscountAmount)
            // );
            tempText = "Kampanjpris!";
            if (prices[i].onlyMembers) {
              tempText += " Kortvara";
            }
          } else if (this.props.showComparePrices && prices[i].compareAmount) {
            amChartData.push({
              date: prices[i].date,
              price: this.formatAmountToPrice(prices[i].compareAmount),
            });
            // yData.push(this.formatAmountToPrice(prices[i].compareAmount));
          } else {
            amChartData.push({
              date: prices[i].date,
              price: this.formatAmountToPrice(prices[i].amount),
            });
            // yData.push(this.formatAmountToPrice(prices[i].amount));
          }
          text.push(tempText);
        }
        data.visible = stores[0].active;
        data.amChartData = amChartData;
        data.text = text;
        returnData.push(data);
      });
    });
    return returnData;
  }

  averageInnerPrices(pricesArray) {
    if (pricesArray.length <= 1) {
      return pricesArray[0];
    }

    const flat = pricesArray
      .flatMap((p) => p)
      .flatMap((p) => p)
      .filter((p) => p);
    const groupedByDate = {};
    flat.forEach((p) => {
      if (!groupedByDate[p.date]) {
        groupedByDate[p.date] = [];
      }
      groupedByDate[p.date].push(p);
    });

    const avgResult = [];
    Object.values(groupedByDate).forEach((v) => {
      const avg = v[0].clone();
      const amount = v
        .filter((x) => x.amount)
        .map((x) => x.amount)
        .reduce((acc, val) => [acc[0] + val, acc[1] + 1], [0, 0]);
      const compareAmount = v
        .filter((x) => x.compareAmount)
        .map((x) => x.compareAmount)
        .reduce((acc, val) => [acc[0] + val, acc[1] + 1], [0, 0]);
      const discountAmount = v
        .filter((x) => x.discountAmount)
        .map((x) => x.discountAmount)
        .reduce((acc, val) => [acc[0] + val, acc[1] + 1], [0, 0]);
      const compareDiscountAmount = v
        .filter((x) => x.compareDiscountAmount)
        .map((x) => x.compareDiscountAmount)
        .reduce((acc, val) => [acc[0] + val, acc[1] + 1], [0, 0]);
      const onlyMembers = v.some((x) => x.onlyMembers);

      avg.amount = amount[1] ? amount[0] / amount[1] : null;
      avg.compareAmount = compareAmount[1] ? compareAmount[0] / compareAmount[1] : null;
      avg.discountAmount = discountAmount[1] ? discountAmount[0] / discountAmount[1] : null;
      avg.compareDiscountAmount = compareDiscountAmount[1] ? compareDiscountAmount[0] / compareDiscountAmount[1] : null;
      avg.onlyMembers = onlyMembers;
      avgResult.push(avg);
    });

    return [avgResult];
  }

  createPlotData(productData) {
    const map = new Map();
    for (const data of productData) {
      this.createPlotDataHelper(data, map);
    }
    const result = [];
    const weeks = new Set();

    map.forEach((value, key) => {
      result.push({ date: key, ...value });
      weeks.add(key.substring(0, 4) + getWeekFromDateString(key));
    });
    this.setState({
      weeksCount: weeks.size,
    });

    return result;
  }

  parseDate(dateStr) {
    const parts = dateStr.split("-");
    return new Date(parts[2], parts[1] - 1, parts[0]);
  }

  createPlotDataHelper(data, map) {
    const amChartData = data.amChartData;
    const name = data.name;
    for (let i = 0; i < amChartData.length; i++) {
      if (map.has(amChartData[i].date)) {
        const currentValue = map.get(amChartData[i].date);
        const key = name.split(" ").join("");
        if (key in currentValue) {
          const oldValue = currentValue[key];
          const newValue = (parseFloat(oldValue) + parseFloat(amChartData[i].price)) / 2;
          currentValue[key] = newValue.toString();
          map.set(amChartData[i].date, currentValue);
        } else {
          const valueToAdd = { [key]: amChartData[i].price };
          const newValue = { ...currentValue, ...valueToAdd };
          map.set(amChartData[i].date, newValue);
        }
      } else {
        map.set(amChartData[i].date, {
          [name.split(" ").join("")]: amChartData[i].price,
        });
      }
    }
  }

  createLayoutForStore = (stores) => {
    let layout;
    let chain = stores[0].chainName || this.props.intl.formatMessage({ id: "store.other" });
    if (chainTypeToString(stores[0].type) != chain) {
      chain += " " + chainTypeToString(stores[0].type);
    }
    const name = stores.length == 1 ? chain + " " + stores[0].name : chain;

    if (this.props.showComparePrices && this.props.isSameUnit) {
      layout = this.getStandardLayout(this.getUnit(stores[0]));
      layout.title = name + " - " + this.props.intl.formatMessage({ id: "priceTable.showsComparePrice" });
      return layout;
    }
    layout = this.getStandardLayout();
    layout.title = name;
    return layout;
  };

  // this.props.isSameUnit checks that this expression is valid
  getUnit(store) {
    const products = store.getActiveProducts();
    if (!products || products.length == 0) {
      return null;
    }

    return products[0].prices[0][0].unit;
  }

  getStandardLayout = (unit) => {
    const margins = 48;
    const layout = {
      autosize: true,
      width: "98%",
      height: this.props.height || 300,
      showlegend: true,
      legend: {
        x: 100,
        y: 1,
        xAnchor: "left",
        yAnchor: "bottom",
        traceorder: "normal",
      },
      margin: {
        r: margins,
        l: margins + 32,
        b: margins,
        t: margins + 48,
        pad: 1,
      },
      xaxis: {
        title: "Datum",
      },
    };
    if (unit) {
      layout.yaxis = {
        title: this.props.intl.formatMessage({ id: "priceTable.comparePrice" }),
        ticksuffix: " kr/" + this.unitConvertion[unit],
      };
    } else {
      layout.yaxis = {
        title: "Pris",
        ticksuffix: " kr",
      };
    }
    return layout;
  };

  createGraphs(plotData, store, plotLayout) {
    const graphs = [];
    plotData.forEach((data, index) => {
      graphs.push(this.createGraphsHelper(data, plotLayout.yaxis.ticksuffix, index));
    });
    return graphs;
  }

  createGraphsHelper(data, ticksuffix, index) {
    return {
      id: "graph" + data.name.split(" ").join(""),
      balloon: {
        verticalPadding: 3,
      },
      balloonText: "<b>" + data.productName + "</b>: [[value]] " + ticksuffix,
      type: "step",
      lineThickness: 2,
      lineColor: CHART_COLORS[index % CHART_COLORS.length],
      bullet: "square",
      bulletAlpha: 0,
      bulletSize: 2,
      bulletBorderAlpha: 0,
      connect: false,
      title: data.name,
      legendLabel: data.productName,
      valueField: data.name.split(" ").join(""),
    };
  }

  handleChartInit = (e) => {
    this.chartInstance = e.chart;
    this.lockedBulletItems = [];
    if (e.chart.chartDiv && !e.chart.chartDiv._cursorLockBound) {
      e.chart.chartDiv._cursorLockBound = true;
      e.chart.chartDiv.addEventListener("click", this.handleChartAreaClick);
      e.chart.chartDiv.addEventListener("mouseleave", this.handleChartMouseLeave);
    }
    this.restoreCursorLock(e.chart);
  };

  restoreCursorLock = (chart) => {
    if (!this.cursorLocked || !this.lockedCategory || !chart) {
      this.lockedBulletItems = [];
      return;
    }

    const dataProvider = chart.dataProvider || this.state.plotData || [];
    const index = dataProvider.findIndex((item) => item && item.date === this.lockedCategory);
    if (index < 0) {
      this.cursorLocked = false;
      this.lockedCursorIndex = null;
      this.lockedCategory = null;
      this.lockedBulletItems = [];
      this.setLegendValues({});
      return;
    }

    this.lockedCursorIndex = index;
    this.lockedBulletItems = [];
    if (chart.chartCursor) {
      chart.chartCursor.leaveCursor = true;
      chart.chartCursor.forceShow = true;
      try {
        chart.chartCursor.showCursorAt(this.lockedCategory);
      } catch (err) {
        // Chart may not be fully ready
      }
    }
    this.updateLegendFromIndex(chart, index);
    requestAnimationFrame(() => {
      this.applyLockedBullets(chart);
      this.hideHoverBulletsExceptLocked(chart);
    });
  };

  setLegendValues = (valuesById) => {
    Object.keys(this.legendValueEls).forEach((graphId) => {
      const el = this.legendValueEls[graphId];
      if (el) {
        el.textContent = valuesById[graphId] || "";
      }
    });
  };

  resetBulletAppearance = (dItem, bullet) => {
    if (!bullet || !dItem) {
      return;
    }
    const graph = dItem.graph;
    const alpha = graph && !isNaN(graph.bulletAlpha) ? graph.bulletAlpha : 0;
    const borderAlpha = graph && !isNaN(graph.bulletBorderAlpha) ? graph.bulletBorderAlpha : 0;
    bullet.doNotScale = false;
    try {
      bullet.translate(dItem.bx, dItem.by, 1);
      bullet.setAttr("fill-opacity", alpha);
      bullet.setAttr("stroke-opacity", borderAlpha);
    } catch (err) {
      // Chart may have been redrawn
    }
  };

  clearLockedBullets = () => {
    (this.lockedBulletItems || []).forEach(({ dItem, bullet }) => {
      this.resetBulletAppearance(dItem, bullet);
    });
    this.lockedBulletItems = [];
  };

  hideHoverBulletsExceptLocked = (chart) => {
    if (!chart) {
      return;
    }
    if (chart.chartCursor && typeof chart.chartCursor.fixBulletSize === "function") {
      chart.chartCursor.fixBulletSize();
    }
    const lockedBullets = new Set((this.lockedBulletItems || []).map((item) => item.bullet));
    if (!chart.chartData) {
      return;
    }
    chart.chartData.forEach((serialDataItem) => {
      const axes = (serialDataItem && serialDataItem.axes) || {};
      Object.keys(axes).forEach((axisId) => {
        const graphsMap = axes[axisId].graphs || {};
        Object.keys(graphsMap).forEach((graphId) => {
          const dItem = graphsMap[graphId];
          if (!dItem || !dItem.bulletGraphics || lockedBullets.has(dItem.bulletGraphics)) {
            return;
          }
          this.resetBulletAppearance(dItem, dItem.bulletGraphics);
        });
      });
    });
  };

  hideHoverBalloonsExceptLocked = (chart) => {
    if (!chart) {
      return;
    }
    (chart.graphs || []).forEach((graph) => {
      if (graph && typeof graph.hideBalloonReal === "function") {
        graph.hideBalloonReal();
      } else if (graph && typeof graph.hideBalloon === "function") {
        graph.hideBalloon();
      }
    });
    if (typeof chart.hideBalloon === "function") {
      chart.hideBalloon();
    } else if (chart.balloon && typeof chart.balloon.hide === "function") {
      chart.balloon.hide();
    }
    if (this.cursorLocked && chart.chartCursor && this.lockedCategory != null) {
      this._snappingCursor = true;
      chart.chartCursor.forceShow = true;
      chart.chartCursor.showCursorAt(this.lockedCategory);
      this._snappingCursor = false;
    }
  };

  applyLockedBullets = (chart) => {
    const index = this.lockedCursorIndex;
    if (index == null || !chart || !chart.chartData || !chart.chartData[index]) {
      return;
    }
    const scale = (chart.chartCursor && chart.chartCursor.graphBulletSize) || 1.7;

    if (!this.lockedBulletItems.length) {
      const axes = chart.chartData[index].axes || {};
      Object.keys(axes).forEach((axisId) => {
        const graphsMap = axes[axisId].graphs || {};
        Object.keys(graphsMap).forEach((graphId) => {
          const dItem = graphsMap[graphId];
          if (!dItem || !dItem.bulletGraphics || (dItem.graph && dItem.graph.hidden)) {
            return;
          }
          this.lockedBulletItems.push({ dItem, bullet: dItem.bulletGraphics });
        });
      });
    }

    this.lockedBulletItems.forEach(({ dItem, bullet }) => {
      if (!bullet || !dItem) {
        return;
      }
      bullet.doNotScale = true;
      try {
        bullet.translate(dItem.bx, dItem.by, scale);
        bullet.setAttr("fill-opacity", 1);
        bullet.setAttr("stroke-opacity", 1);
      } catch (err) {
        // Chart may have been redrawn
      }
    });
  };

  updateLegendFromIndex = (chart, index) => {
    if (index == null || !chart || !chart.dataProvider) {
      this.setLegendValues({});
      return;
    }
    const dataItem = chart.dataProvider[index];
    if (!dataItem) {
      this.setLegendValues({});
      return;
    }
    const ticksuffix = (this.state.plotLayout.yaxis && this.state.plotLayout.yaxis.ticksuffix) || "";
    const legendValues = {};
    (this.state.graphs || []).forEach((graph) => {
      const value = dataItem[graph.valueField];
      if (value != null && value !== "") {
        legendValues[graph.id] = value + ticksuffix;
      }
    });
    this.setLegendValues(legendValues);
  };

  handleChartAreaClick = () => {
    const chart = this.chartInstance;
    if (!chart || !chart.chartCursor) {
      return;
    }

    if (this.cursorLocked) {
      this.cursorLocked = false;
      this.lockedCursorIndex = null;
      this.lockedCategory = null;
      chart.chartCursor.leaveCursor = false;
      this.clearLockedBullets();
      this.hideHoverBulletsExceptLocked(chart);
      this.hideHoverBalloonsExceptLocked(chart);
      return;
    }

    const index = chart.chartCursor.index;
    if (index == null || !chart.dataProvider || !chart.dataProvider[index]) {
      return;
    }

    this.cursorLocked = true;
    this.lockedCursorIndex = index;
    this.lockedCategory = chart.dataProvider[index].date;
    chart.chartCursor.leaveCursor = true;
    this.updateLegendFromIndex(chart, index);
    this.applyLockedBullets(chart);
    this.hideHoverBulletsExceptLocked(chart);
  };

  handleChartMouseLeave = () => {
    const chart = this.chartInstance;
    if (!chart) {
      return;
    }
    this.hideHoverBulletsExceptLocked(chart);
    this.hideHoverBalloonsExceptLocked(chart);
    if (this.cursorLocked) {
      this.applyLockedBullets(chart);
      return;
    }
    this.setLegendValues({});
  };

  handleCursorChanged = (e) => {
    if (this.cursorLocked) {
      if (!this._snappingCursor && this.lockedCategory != null && e.index !== this.lockedCursorIndex && e.chart && e.chart.chartCursor) {
        this._snappingCursor = true;
        e.chart.chartCursor.showCursorAt(this.lockedCategory);
        this._snappingCursor = false;
      }
      if (e.chart && e.chart.chartCursor && typeof e.chart.chartCursor.fixBulletSize === "function") {
        e.chart.chartCursor.fixBulletSize();
      }
      this.applyLockedBullets(e.chart);
      return;
    }
    this.updateLegendFromIndex(e.chart, e.index);
  };

  handleCursorHidden = () => {
    this.handleChartMouseLeave();
  };

  toggleLegendGraph = (graphId) => {
    const chart = this.chartInstance;
    if (!chart || !chart.graphs) {
      return;
    }
    const graph = chart.graphs.find((item) => item.id === graphId);
    if (!graph) {
      return;
    }
    const willHide = !graph.hidden;
    if (willHide) {
      chart.hideGraph(graph);
    } else {
      chart.showGraph(graph);
    }
    const itemEl = this.legendItemEls[graphId];
    if (itemEl) {
      itemEl.classList.toggle("is-hidden", willHide);
    }
  };

  getVisibleExportGraphs = () => {
    return (this.state.graphs || []).filter((graph) => {
      const chartGraph = this.chartInstance && this.chartInstance.graphs && this.chartInstance.graphs.find((item) => item.id === graph.id);
      return !(chartGraph && chartGraph.hidden);
    });
  };

  getExportLegendValues = () => {
    const chart = this.chartInstance;
    let index = null;
    if (this.cursorLocked && this.lockedCursorIndex != null) {
      index = this.lockedCursorIndex;
    } else if (chart && chart.chartCursor && chart.chartCursor.index != null) {
      index = chart.chartCursor.index;
    } else if (chart && chart.dataProvider && chart.dataProvider.length) {
      index = chart.dataProvider.length - 1;
    }

    if (index != null && chart && chart.dataProvider && chart.dataProvider[index]) {
      const dataItem = chart.dataProvider[index];
      const ticksuffix = (this.state.plotLayout.yaxis && this.state.plotLayout.yaxis.ticksuffix) || "";
      const legendValues = {};
      (this.state.graphs || []).forEach((graph) => {
        const value = dataItem[graph.valueField];
        if (value != null && value !== "") {
          legendValues[graph.id] = value + ticksuffix;
        }
      });
      return legendValues;
    }

    const legendValues = {};
    Object.keys(this.legendValueEls || {}).forEach((graphId) => {
      const el = this.legendValueEls[graphId];
      if (el && el.textContent) {
        legendValues[graphId] = el.textContent;
      }
    });
    return legendValues;
  };

  addLegendToFabricExport = (exportPlugin) => {
    const fabricCanvas = exportPlugin.setup.fabric;
    const chartWidth = fabricCanvas.getWidth();
    const chartHeight = fabricCanvas.getHeight();
    const labelOffset = 28;
    const priceGap = 8;
    const rightPadding = 6;
    const minLegendWidth = 240;
    const lineHeight = 16;
    const graphs = this.getVisibleExportGraphs();
    const legendValues = this.getExportLegendValues();
    const neededHeight = Math.max(chartHeight, 40 + graphs.length * lineHeight);
    const fontFamily = (exportPlugin.setup.chart && exportPlugin.setup.chart.fontFamily) || "Arial";
    const fontSize = 11;

    const cleanLabel = (title) =>
      String(title || "")
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<[^>]+>/g, "");

    let maxLabelWidth = 0;
    let maxPriceWidth = 0;
    const rows = graphs.map((graph) => {
      const label = cleanLabel(graph.title);
      maxLabelWidth = Math.max(maxLabelWidth, new fabric.Text(label, { fontSize, fontFamily }).width);
      const price = legendValues[graph.id] || "";
      if (price) {
        maxPriceWidth = Math.max(maxPriceWidth, new fabric.Text(price, { fontSize, fontFamily }).width);
      }
      return { graph, label, price };
    });

    const legendWidth = Math.max(minLegendWidth, labelOffset + maxLabelWidth + priceGap + maxPriceWidth + rightPadding);
    const labelLeft = chartWidth + labelOffset;

    fabricCanvas.setWidth(chartWidth + legendWidth);
    fabricCanvas.setHeight(neededHeight);

    let y = 40;
    rows.forEach(({ graph, label, price }) => {
      fabricCanvas.add(
        new fabric.Line([chartWidth + 6, y + 7, chartWidth + 20, y + 7], {
          stroke: graph.lineColor || "#999",
          strokeWidth: 3,
          selectable: false,
        }),
      );
      fabricCanvas.add(
        new fabric.Text(label, {
          left: labelLeft,
          top: y,
          fontSize,
          fontFamily,
          fill: "#000000",
          selectable: false,
        }),
      );
      if (price) {
        fabricCanvas.add(
          new fabric.Text(price, {
            left: chartWidth + legendWidth - rightPadding,
            top: y,
            fontSize,
            fontFamily,
            fill: "#000000",
            originX: "right",
            selectable: false,
          }),
        );
      }
      y += lineHeight;
    });
  };

  renderHtmlLegend(chartHeight) {
    const graphs = this.state.graphs || [];
    return (
      <div className="chart-legend-scroll" style={{ height: chartHeight, maxHeight: chartHeight }}>
        {graphs.map((graph) => (
          <div
            className="chart-legend-item"
            key={graph.id || graph.title}
            ref={(el) => {
              this.legendItemEls[graph.id] = el;
            }}
            onClick={() => this.toggleLegendGraph(graph.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(evt) => {
              if (evt.key === "Enter" || evt.key === " ") {
                evt.preventDefault();
                this.toggleLegendGraph(graph.id);
              }
            }}>
            <span className="chart-legend-marker" style={{ backgroundColor: graph.lineColor || "#999" }} />
            <span className="chart-legend-label" dangerouslySetInnerHTML={{ __html: graph.title }} />
            <span
              className="chart-legend-value"
              ref={(el) => {
                this.legendValueEls[graph.id] = el;
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  render() {
    const id = this.state.id;
    const exportMenuId = `export-menu-${id}`;
    const fileName = this.state.fileName;
    const chartHeight = this.state.plotLayout.height || 300;
    const plot = this;
    return (
      <Paper elevation={0} className={this.props.className}>
        <div className="chart-with-legend">
          {this.renderHtmlLegend(Math.max(chartHeight - 52, 120))}
          <div className="chart-with-legend-plot">
            <AmCharts.React
              style={{
                width: "100%",
                height: chartHeight,
              }}
              options={{
                type: "serial",
                hideCredits: true,
                titles: [{ text: this.state.plotLayout.title }],
                theme: "light",
                autoMarginOffset: 25,
                dataProvider: this.state.plotData,
                graphs: this.state.graphs,
                listeners: this.chartListeners,
                chartCursor: {
                  fullWidth: false,
                  cursorAlpha: 0.05,
                  graphBulletAlpha: 1,
                  listeners: this.cursorListeners,
                },
                dataDateFormat: "YYYY-MM-DD",
                categoryField: "date",
                categoryAxis: {
                  autoGridCount: false,
                  showFirstLabel: true,
                  showLastLabel: true,
                  minPeriod: "DD",
                  parseDates: false,
                  equalSpacing: false,
                  autoGridCount: false,
                  gridCount: Math.min(this.state.weeksCount - 2, getMaxGridCount()),
                  categoryFunction: getShortDateWithWeek,
                  gridAlpha: 0,
                },
                valueAxes: [
                  {
                    position: "left",
                    title: this.state.plotLayout.yaxis.title,
                    unit: this.state.plotLayout.yaxis.ticksuffix,
                    axisAlpha: 0,
                  },
                ],
                export: {
                  enabled: true,
                  fileName: fileName,
                  divId: exportMenuId,
                  removeImages: false,
                  forceRemoveImages: false,
                  position: "bottom-right",
                  menu: [
                    {
                      class: "export-main",
                      menu: [
                        { label: "PNG image", format: "PNG" },
                        { label: "JPG image", format: "JPG" },
                        { label: "PDF document", format: "PDF" },
                        { label: "CSV data", format: "CSV" },
                        { label: "Excel spreadsheet", format: "XLSX" },
                        { label: "Print chart", format: "PRINT" },
                        {
                          label: "Annotate…",
                          action: "draw",
                          menu: [
                            {
                              class: "export-drawing",
                              menu: [
                                { label: "Save as PNG", format: "PNG" },
                                { label: "Save as JPG", format: "JPG" },
                                { label: "Print", format: "PRINT" },
                                { label: "Cancel", format: "CANCEL" },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    {
                      class: "export-png",
                      label: "PNG",
                      title: "Download PNG",
                      format: "PNG",
                    },
                  ],
                  afterCapture: function afterCapture(menuConfig) {
                    const chartWidthBeforeLegend = this.setup.fabric.getWidth();
                    plot.addLegendToFabricExport(this);

                    const imgFromHtml = document.getElementById(id);
                    if (imgFromHtml) {
                      imgFromHtml.crossOrigin = "anonymous";
                      const fabricImage = new fabric.Image(imgFromHtml, {
                        top: 5,
                        left: chartWidthBeforeLegend * 0.8,
                      });
                      this.setup.fabric.add(fabricImage);
                    }

                    const weekNumber = (today) => {
                      const onejan = new Date(today.getFullYear(), 0, 1);
                      return Math.ceil(((today - onejan) / 86400000 + onejan.getDay() + 1) / 7);
                    };
                    const text = new fabric.Text("Vecka " + weekNumber(new Date()), {
                      top: 15,
                      left: 15,
                      fontFamily: this.setup.chart.fontFamily,
                      fontSize: 15,
                      fontWeight: "bold",
                    });
                    this.setup.fabric.add(text);
                  },
                },
              }}
            />
          </div>
          <div id={exportMenuId} className="chart-export-menu-host" />
        </div>
      </Paper>
    );
  }
}

export default injectIntl(StorePricePlot);
